/**
 * useMaterialsData - Manages grade materials data
 *
 * This hook manages the list of materials (worksheets, resources) for a grade.
 * Used by GradeMaterialsContainer.
 *
 * Follows the URL-as-source-of-truth pattern established by useTermsData:
 * - Auto-fetches when urlGradeCode changes AND `enabled` is true
 * - Uses ref-based deduplication to avoid redundant fetches
 *
 * `enabled` lets the caller fetch only when the page needs materials.
 */

import { useState, useEffect, useRef } from "react";
import { gradeMaterialsApi } from "@/api/gradeMaterialsApi";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import type { Material } from "@/ts/interfaces/material/api/gradeMaterialsApiInterfaces";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";

interface UseMaterialsDataParams {
  urlGradeCode: number | undefined;
  enabled?: boolean;
}

export interface MaterialsDataState {
  // Data
  materials: Material[];

  // Status
  materialsStatus: REQUEST_STATUS;
  materialsError: ErrorInterface | null;
}

export type MaterialsData = MaterialsDataState;

export function useMaterialsData({
  urlGradeCode,
  enabled = false
}: UseMaterialsDataParams): MaterialsData {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [materialsStatus, setMaterialsStatus] = useState<REQUEST_STATUS>(
    REQUEST_STATUS.IDLE
  );
  const [materialsError, setMaterialsError] = useState<ErrorInterface | null>(
    null
  );

  const lastFetchedGradeRef = useRef<number | undefined>(undefined);

  // Auto-fetch when the target grade changes AND materials are needed.
  useEffect(() => {
    if (!urlGradeCode || !enabled) {
      setMaterials([]);
      setMaterialsStatus(REQUEST_STATUS.IDLE);
      setMaterialsError(null);
      lastFetchedGradeRef.current = undefined;
      return;
    }

    // Already fetched for this grade during the current cache window.
    if (lastFetchedGradeRef.current === urlGradeCode) {
      return;
    }

    lastFetchedGradeRef.current = urlGradeCode;
    setMaterialsStatus(REQUEST_STATUS.PENDING);
    setMaterialsError(null);

    gradeMaterialsApi
      .getGradeMaterials(urlGradeCode)
      .then(response => {
        // Guard against stale responses when grade changed mid-flight
        if (lastFetchedGradeRef.current !== urlGradeCode) return;
        setMaterials(response.materials ?? []);
        setMaterialsStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        // Guard against stale responses when grade changed mid-flight
        if (lastFetchedGradeRef.current !== urlGradeCode) return;
        console.error("Error fetching materials:", error);
        setMaterialsStatus(REQUEST_STATUS.ERROR);
        lastFetchedGradeRef.current = undefined;

        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        setMaterialsError({
          code: err?.customAttributes?.httpErrorStatus ?? 500,
          message: err?.message ?? "Failed to fetch materials"
        });
      });
  }, [urlGradeCode, enabled]);

  return {
    // State
    materials,
    materialsStatus,
    materialsError
  };
}
